// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useMemo } from 'react';
import type { EsgProps, EsgScores } from '../components/esg';
import { type Stock } from '@shared/data-access/interfaces/api/ai/screener';
import { useTranslation } from 'react-i18next';

const exposureFilter = [
  'adult_content',
  'alcoholic',
  'animal_testing',
  'coal_energy',
  'firearms',
  'fur_leather',
  'gambling',
  'gmo',
  'nuclear',
  'pesticides',
  'tobacco',
];

export const useEsg = ({ assetsInfo, esgAnalysis }: EsgProps) => {
  const { t } = useTranslation();
  const { metrics } = esgAnalysis;
  const metricsLength = metrics.length;

  const esgScores: EsgScores = useMemo(
    () => ({
      environmentScore: metrics[metricsLength - 4].value,
      governanceScore: metrics[metricsLength - 3].value,
      socialScore: metrics[metricsLength - 2].value,
      totalEsgScore: metrics[metricsLength - 1].value,
    }),
    [metrics, metricsLength],
  );

  const exposure = useMemo(
    () =>
      esgAnalysis.metrics.filter(metric => exposureFilter.includes(metric.name)),
    [esgAnalysis],
  );

  const esgKpiData = useMemo(
    () => [
      {
        value: esgScores.totalEsgScore,
        text: t('research.esg.total-score', { ns: 'research' }),
        icon: 'Shield' as const,
        color: 'main' as const,
      },
      {
        value: esgScores.environmentScore,
        text: t('research.esg.environmental', { ns: 'research' }),
        icon: 'Leaf' as const,
        color: 'success' as const,
      },
      {
        value: esgScores.socialScore,
        text: t('research.esg.social', { ns: 'research' }),
        icon: 'Users' as const,
        color: 'secondary' as const,
      },
      {
        value: esgScores.governanceScore,
        text: t('research.esg.governance', { ns: 'research' }),
        icon: 'Landmark' as const,
        color: 'main' as const,
      },
    ],
    [esgScores, t],
  );

  const topAssets = useMemo(() => {
    return esgAnalysis.top_assets.map(asset => {
      const assetData = assetsInfo?.find(
        assetInfo => assetInfo.ticker === asset.ticker,
      ) as Stock;

      return {
        name: assetData?.name || '',
        ticker: asset.ticker,
        url_logo: assetData?.url_logo,
        weight: asset.weight,
        environmentScore: asset.environment_score,
        governanceScore: asset.governance_score,
        socialScore: asset.social_score,
        totalEsgScore: asset.total_esg_score,
        url: assetData?.url_morningstar,
      };
    });
  }, [esgAnalysis.top_assets, assetsInfo]);

  return {
    exposure,
    esgKpiData,
    topAssets,
  };
};
